import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { breadcrumb } from "./seo";

const VALUES: { title: string; body: string }[] = [
  {
    title: "Ship the calm version",
    body: "We build software that feels serene to use. Fewer knobs, clearer states, no surprises — for hiring teams and candidates alike.",
  },
  {
    title: "Evidence over opinion",
    body: "Every decision our product informs is backed by what a candidate actually said and did. Vibes don't make the call.",
  },
  {
    title: "Automate the busywork",
    body: "Scheduling, reminders, proctoring, first-pass scoring — if a machine can do it well, a human shouldn't have to.",
  },
  {
    title: "Fairness is a feature",
    body: "Structured, consistent, integrity-checked interviews give every candidate the same fair shot. That's the whole point.",
  },
];

const STEPS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "Create an interview",
    body: "Describe the role and the questions that matter. Your AI interviewer is ready in minutes — no scripting marathons.",
  },
  {
    step: "02",
    title: "Invite & auto-schedule",
    body: "Send a link and candidates self-schedule by email. Reminders, time zones and no-shows are handled for you.",
  },
  {
    step: "03",
    title: "Get a scored evaluation",
    body: "Each interview returns a structured, evidence-backed evaluation with integrity checks — so you meet only the best.",
  },
];

const STACK: { name: string; what: string }[] = [
  { name: "AI video interviews", what: "Real-time conversation, transcription and proctoring" },
  { name: "AI evaluations", what: "Structured scoring via a model gateway" },
  { name: "Transactional email", what: "Invites, scheduling and reminders that land" },
  { name: "Stripe billing", what: "Simple, pay-as-you-go usage" },
  { name: "Cloudflare edge", what: "A fast global network with managed Postgres" },
];

export function About() {
  return (
    <MarketingPage
      seo={{
        title: "About — WonderfulInterview",
        description:
          "WonderfulInterview is a pay-as-you-go AI interview platform from Wonderful Agent. We automate scheduling, conducting, proctoring and scoring so teams meet only the people worth meeting.",
        path: "/about",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]),
      }}
    >
      {/* Header */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-80"
          style={{
            background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.5) 0%, rgba(244,201,155,0) 70%)",
          }}
        />
        <Container className="relative pt-20 pb-12 text-center sm:pt-28">
          <Eyebrow>About us</Eyebrow>
          <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Making rigorous interviews{" "}
            <span className="font-display italic font-normal text-warm-accent">fair, fast and human</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">
            WonderfulInterview is a pay-as-you-go AI interview platform — a Wonderful Agent product. We automate
            the recruiter-coordination layer so teams meet only the people worth meeting, and every candidate gets
            the same structured shot.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <MButton to="/careers">
              Join the team <ArrowRight />
            </MButton>
            <MButton to="/platform" variant="outline">
              Explore the platform
            </MButton>
          </div>
        </Container>
      </section>

      {/* Our mission */}
      <section className="py-24">
        <Container>
          <div className="mx-auto max-w-2xl">
            <Eyebrow>Our mission</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Interviews should reward the candidate, not the calendar.
            </h2>
            <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-warm-muted">
              <p>
                Traditional interviews are inconsistent by design. Two candidates rarely get the same questions, the
                same attention, or the same fair read — and the noise of bias and improvisation creeps in long before
                anyone reaches a decision. Talented people slip through for reasons that have nothing to do with the
                job.
              </p>
              <p>
                We think every candidate deserves a structured, evidence-backed process: the same questions, the same
                rubric, the same shot. When an interview is consistent and integrity-checked, the signal gets clearer
                and the outcome gets fairer for everyone involved.
              </p>
              <p>
                And hiring teams shouldn't drown in scheduling. By automating the coordination layer — scheduling,
                conducting, proctoring and scoring — we free people to spend their judgment where it actually
                matters: meeting the candidates worth meeting.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* What we believe */}
      <section className="bg-warm-sand/40 py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>What we believe</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              A few things we hold to.
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <h3 className="text-xl font-semibold text-warm-ink">{v.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{v.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* How it actually works */}
      <section className="py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>How it actually works</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Three steps, then a decision.
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.step} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <span className="font-display text-3xl italic text-warm-accent">{s.step}</span>
                <h3 className="mt-4 text-xl font-semibold text-warm-ink">{s.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <MButton to="/platform" variant="outline">
              See the full platform <ArrowRight />
            </MButton>
          </div>
        </Container>
      </section>

      {/* Built on a modern stack */}
      <section className="bg-warm-sand/40 py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Under the hood</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Built on a modern stack.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-warm-muted">
              Powered by a handful of technologies we trust, each doing one thing well. We use these tools — no
              partnership or endorsement implied.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {STACK.map((t) => (
              <div
                key={t.name}
                className="rounded-3xl border border-warm-border bg-warm-card px-6 py-5"
              >
                <div className="text-base font-semibold text-warm-ink">{t.name}</div>
                <div className="mt-1.5 text-sm leading-relaxed text-warm-muted">{t.what}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* The team */}
      <section className="py-24">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Eyebrow>The team</Eyebrow>
              <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
                Small, senior, fully remote.
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-warm-muted">
                We're a tight, experienced team spread across time zones, united by a stubborn belief in calm
                software and fair hiring. We work async, hold a high bar, and ship the version we'd want to use
                ourselves.
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-warm-muted">
                If that sounds like your kind of place, we'd love to meet you.
              </p>
              <div className="mt-8">
                <MButton to="/careers">
                  See open roles <ArrowRight />
                </MButton>
              </div>
            </div>
            <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
              <img
                src={IMG.team}
                alt="A diverse group of professionals collaborating and smiling in a warm modern office"
                className="h-[320px] w-full object-cover sm:h-[440px]"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="pb-24">
        <Container>
          <div className="rounded-4xl bg-warm-ink px-8 py-16 text-center text-warm-bg sm:px-16">
            <h2 className="mx-auto max-w-2xl text-4xl font-bold tracking-tight sm:text-5xl">
              Meet only the people{" "}
              <span className="font-display italic font-normal text-warm-glow">worth meeting</span>.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-warm-bg/70">
              Spin up your first AI interview in minutes. Pay only for what you use — no seats, no contracts.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <MButton to="/signup">
                Start free <ArrowRight />
              </MButton>
              <MButton href="mailto:hello@wonderfulagent.ai" variant="outline">
                Talk to us
              </MButton>
            </div>
          </div>
        </Container>
      </section>
    </MarketingPage>
  );
}
